/**
 * Session-end contracts for deliberate host close vs transient connection failure.
 *
 * Deliberate end uses WebRTC datachannel:
 *   { "__peerClose": true, "reason": "host:…" }
 */

export const PEER_CLOSE_MESSAGE_FLAG = '__peerClose' as const;

export type PeerCloseMessage = {
  [PEER_CLOSE_MESSAGE_FLAG]: true;
  reason: string;
};

export function isPeerCloseMessage(msg: unknown): msg is PeerCloseMessage {
  if (typeof msg !== 'object' || msg === null) return false;
  const record = msg as Record<string, unknown>;
  return record[PEER_CLOSE_MESSAGE_FLAG] === true && typeof record['reason'] === 'string';
}

/**
 * Legacy host-initiated reasons older builds emit WITHOUT the `host:` prefix.
 */
export const LEGACY_HOST_INITIATED_REASONS: ReadonlySet<string> = new Set([
  'game_closed',
  'peer_connection_timeout',
  'duplicate_peer',
  'host_app_closed',
  'all_players_left_restart',
  'all_players_left_no_restart',
  'campaign_clear:active_match',
  'pavilion:no_players_timeout',
  'init:timeout',
  'invitation:invalidated',
  'ipc:stopstream',
  'coordinator_requested',
  'stream_stopped',
  'admin_session_end',
  'user-requested',
  'cancelled',
]);

/** Ensure host-initiated reasons carry the canonical `host:` prefix. */
export function normalizeHostEndReason(reason: string): string {
  const trimmed = reason.trim();
  if (!trimmed) return 'host:close';
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('host:')) return trimmed;
  if (lower.startsWith('ad_mode:')) return trimmed;
  if (LEGACY_HOST_INITIATED_REASONS.has(lower)) return `host:${trimmed}`;
  // Already a namespaced player/quickstart reason — leave alone.
  if (lower.startsWith('quickstart:') || lower.startsWith('player:')) return trimmed;
  // Default host-initiated closes that forgot the prefix.
  return `host:${trimmed}`;
}

export function isDeliberateSessionEnd(reason?: string): boolean {
  if (!reason) return false;
  const r = reason.toLowerCase();

  if (r.startsWith('host:')) return true;
  if (LEGACY_HOST_INITIATED_REASONS.has(r)) return true;
  if (r.includes('ad_mode:')) return true;
  if (r.includes('quickstart:')) return true;
  if (r.includes('player:close')) return true;
  if (r.includes('kicked')) return true;
  if (r.includes('refused')) return true;
  // ICE credential failures (ice_invalid_credentials) are transient and reclaimable —
  // do not treat every substring "invalid" as a deliberate session end.
  const iceKey = r.replace(/-/g, '_');
  if (r.includes('invalid') && !iceKey.startsWith('ice_')) return true;
  return false;
}

export function isTransientConnectionFailure(reason?: string): boolean {
  if (!reason) return false;
  if (isDeliberateSessionEnd(reason)) return false;

  const r = reason.toLowerCase();
  const iceKey = r.replace(/-/g, '_');

  // host: prefix always wins (including host:… containing "timeout"/"ice").
  if (r.startsWith('host:')) return false;

  if (r.includes('transport_disconnected') || iceKey.includes('transport_disconnected')) return true;
  if (
    iceKey.includes('ice_disconnected') ||
    iceKey.includes('ice_failed') ||
    iceKey.includes('ice_timeout') ||
    iceKey.includes('ice_instant_fail') ||
    iceKey.includes('ice_restart_exhausted') ||
    iceKey.startsWith('ice_') ||
    iceKey === 'ice'
  ) {
    return true;
  }
  if (r.includes('peer_disconnected')) return true;
  if (r.includes('network')) return true;
  if (r.includes('timeout')) return true;
  if (r.includes('connection_lost')) return true;
  if (r.includes('connectionstate:failed')) return true;
  return false;
}

/**
 * True only for transient WebRTC/network failures that should auto-reconnect.
 * Empty / unknown → false (fail closed to Connection lost, not silent retry).
 */
export function isDisconnectRetryable(reason?: string): boolean {
  return isTransientConnectionFailure(reason);
}
